import {ref, computed, unref} from "vue";
import MiniSearch from "minisearch";


/**
 * Process a term (word) for indexing or search query:
 *  * Convert to lowercase string.
 *  * Remove accents and diacritics.
 *
 * @param {string} term Term to process.
 * @returns {string}
 */
function processTerm(term) {
    // Remove accents and diacritics.
    return term.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/**
 * Creates a search over the given set of documents. This function returns a `query` ref that can be used to set
 * the query, e.g. by providing it as a model of search query input. Search will be performed only when the text
 * in the query contains at least `minSearchChars` characters.
 *
 * The function returns two computed properties:
 *  * `searchResults`: contains the list of search results when searching. Will be `null` if the query is too short.
 *  * `searchResultsOrAllItems`: just like `searchResults`, but it will contain all items if the query is too short.
 *
 * Additionally, the `isSearching` property can be used to check if the search was performed.
 *
 * @param {object[]} items List of documents to search in. Can be a computed property, e.g. result of a GraphQL query.
 * @param {string[]} searchFields List of fields that will be searched using full-text search. Required.
 * @param {string[]} searchContainsFields List of fields that will be searched for a case-insensitive match of a substring.
 * @param {number} minSearchChars Minimum length of the query that starts the search. Defaults to 1.
 * @returns {{query: Ref<UnwrapRef<string>, UnwrapRef<string> | string>, searchResults: ComputedRef<null|object[]>, searchResultsOrAllItems: ComputedRef<object[]>, searchResultsOrEmpty: ComputedRef<object[]>, isSearching: ComputedRef<boolean>}}
 */
export function useSearch(items, searchFields, searchContainsFields= [], minSearchChars = 1) {
    const query = ref('');

    const searchDatabase = computed(() => {
        if (searchFields.length === 0) {
            return null;
        }

        const database = new MiniSearch({
            fields: unref(searchFields),
            processTerm,
        });

        // Add all items to the search database.
        const itemsValue = unref(items);
        if (itemsValue !== null && itemsValue !== undefined) {
            database.addAll(itemsValue);
        }

        return database;
    });

    const searchResults = computed(() => {
        if (!query.value || query.value.length < minSearchChars) {
            return null;
        }

        let searchResults;
        if (searchDatabase.value !== null) {
            searchResults = searchDatabase.value.search(query.value, {
                fuzzy: 0.3,
                prefix: true,
            });
        } else {
            searchResults = [];
        }

        const results = [];
        const processedQuery = processTerm(query.value);

        const searchContainsFieldsValue = unref(searchContainsFields);
        const matchedItems = new Set();
        const itemMap = new Map();
        for (const item of unref(items)) {
            itemMap.set(item['id'], item);

            for (const field of searchContainsFieldsValue) {
                if (item[field] && processTerm(item[field]).includes(processedQuery)) {
                    matchedItems.add(item['id']);
                    results.push(item);
                }
            }
        }

        for (const result of searchResults) {
            if (matchedItems.has(result.id)) {
                continue;
            }
            results.push(itemMap.get(result.id));
        }

        return results;
    });

    const searchResultsOrAllItems = computed(() => {
        if (searchResults.value) {
            return searchResults.value;
        } else {
            return unref(items);
        }
    });

    const searchResultsOrEmpty = computed(() => {
        if (searchResults.value) {
            return searchResults.value;
        } else {
            return [];
        }
    });

    const isSearching = computed(() => {
        return searchResults.value !== null;
    });

    return {
        query,
        searchResults,
        searchResultsOrAllItems,
        searchResultsOrEmpty,
        isSearching,
    }
}
